<template>
  <div
    class="relative w-[250px] rounded-xl border border-[#969292]/30 p-4 bg-white flex flex-col min-h-[100px]"
  >
    <!-- Icono de advertencia -->
    <div
      class="absolute top-3 right-3 bg-white rounded-full w-8 h-8 flex items-center justify-center group"
      :class="{
        'text-[#00932A]': cantidad > stock_min,
        'text-[#FFCC00]': cantidad >= 1 && cantidad <= stock_min,
        'text-[#E11607]': cantidad == 0,
      }"
    >
      <svg
        class="w-8 h-8"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          fill-rule="evenodd"
          d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm11-4a1 1 0 1 0-2 0v5a1 1 0 1 0 2 0V8Zm-1 7a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2H12Z"
          clip-rule="evenodd"
        />
      </svg>
      <div
        class="absolute bottom-full right-1/2 translate-x-1/2 mb-2 w-max bg-gray-700 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
      >
        {{ cantidad }} en existencia
      </div>
    </div>

    <!-- Imagen -->
    <img :src="imagen" :alt="nombre" class="mx-auto h-32 object-contain" />

    <!-- Información -->
    <div class="mt-2 ml-2">
      <h2 class="text-base font-semibold text-gray-800">{{ nombre }}</h2>
      <p class="text-[#00932A] text-base font-bold mt-1">$ {{ precio }}</p>
    </div>

    <!-- Botones -->
    <div class="mt-auto flex justify-between items-center pt-4">
      <button
        @click="cantidadEnCarrito === 0 && agregarProducto()"
        :disabled="cantidad === 0"
        :class="{
          'bg-[#00932A] hover:bg-[#007A1F] cursor-pointer':
            cantidad > 0 && cantidadEnCarrito === 0,
          'bg-gray-400 cursor-not-allowed':
            cantidad === 0 && cantidadEnCarrito === 0,
          'bg-green-500 cursor-default': cantidadEnCarrito > 0,
        }"
        class="text-white px-12 py-2 rounded-xl text-sm transition font-semibold flex items-center justify-center gap-2 w-full mr-1"
      >
        <span v-if="cantidadEnCarrito === 0">
          {{ cantidad === 0 ? "Sin stock" : "Agregar" }}
        </span>
        <span v-else class="flex items-center gap-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-1.2 6M17 13l1.2 6M6 19a1 1 0 100 2 1 1 0 000-2zm12 0a1 1 0 100 2 1 1 0 000-2z"
            />
          </svg>
          {{ cantidadEnCarrito }}
        </span>
      </button>

      <button
        class="bg-[#EBEEEA] p-2 px-4 rounded-xl transition"
        @click="mostrarModal = true"
      >
        <svg
          class="w-6 h-6 text-gray-800"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10.779 17.779 4.36 19.918 6.5 13.5m4.279 4.279 8.364-8.643a3.027 3.027 0 0 0-2.14-5.165 3.03 3.03 0 0 0-2.14.886L6.5 13.5m4.279 4.279L6.499 13.5m2.14 2.14 6.213-6.504M12.75 7.04 17 11.28"
          />
        </svg>
      </button>

      <!-- Modal -->
      <div
        v-if="mostrarModal"
        class="fixed inset-0 flex items-center justify-center bg-black/50 z-50"
      >
        <div
          class="bg-white rounded-2xl w-full max-w-lg shadow-xl flex flex-col max-h-[90vh]"
        >
          <!-- Header -->
          <div class="flex justify-between items-center px-6 py-4 border-b">
            <h3 class="text-xl font-semibold text-[#1E5D22]">
              Editar Producto
            </h3>
            <button
              @click="mostrarModal = false"
              class="text-gray-500 hover:text-red-800 text-3xl"
            >
              ✕
            </button>
          </div>

          <!-- Body -->
          <div class="p-6 overflow-y-auto">
            <div class="grid grid-cols-2 gap-5">
              <!-- Stock Actual -->
              <div>
                <label class="block text-sm font-medium mb-2"
                  >Stock Actual</label
                >
                <input
                  type="number"
                  class="border rounded-xl w-full px-3 py-2 bg-gray-100"
                  v-model="editable.cantidad"
                  disabled
                />
              </div>

              <!-- Añadir Stock -->
              <div>
                <label class="block text-sm font-medium mb-2"
                  >Añadir Stock</label
                >
                <input
                  type="number"
                  min="0"
                  class="border rounded-xl w-full px-3 py-2"
                  v-model="editable.cantidad_nueva"
                />
              </div>

              <!-- Baja Stock -->
              <div>
                <label class="block text-sm font-medium mb-2">Baja Stock</label>
                <input
                  type="number"
                  min="0"
                  class="border rounded-xl w-full px-3 py-2"
                  v-model="editable.baja_stock"
                />
              </div>

              <!-- Costo -->
              <div>
                <label class="block text-sm font-medium mb-2">Costo</label>
                <div class="relative">
                  <span
                    class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    $
                  </span>
                  <input
                    type="text"
                    class="border rounded-xl w-full pl-8 pr-3 py-2"
                    v-model="editable.costo"
                  />
                </div>
              </div>

              <!-- Precio -->
              <div>
                <label class="block text-sm font-medium mb-2">Precio</label>
                <div class="relative">
                  <span
                    class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    $
                  </span>
                  <input
                    type="text"
                    class="border rounded-xl w-full pl-8 pr-3 py-2"
                    v-model="editable.precio"
                  />
                </div>
              </div>

              <!-- Comisión -->
              <div>
                <label class="block text-sm font-medium mb-2">Comisión</label>
                <div class="relative">
                  <span
                    class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    %
                  </span>
                  <input
                    type="text"
                    class="border rounded-xl w-full pl-8 pr-3 py-2"
                    v-model="editable.comision"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 border-t">
            <button
              @click="datosEditar"
              class="bg-[#00932A] hover:bg-[#007A1F] text-white px-6 py-2 rounded-xl w-full font-semibold transition"
            >
              Guardar Cambios
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    id: [String, Number],
    nombre: String,
    precio: String,
    imagen: String,
    cantidad: Number,
    cantidad_nueva: Number,
    baja_stock: Number,
    stock: Number,
    costo: String,
    comision: String,
    stock_min: Number,
  },
  data() {
    return {
      mostrarModal: false,
      editable: {
        cantidad: this.cantidad,
        cantidad_nueva: this.cantidad_nueva,
        baja_stock: this.baja_stock,
        costo: this.costo,
        precio: this.precio,
        comision: this.comision,
      },
    };
  },

  methods: {
    agregarProducto() {
      const raw = localStorage.getItem("productosSeleccionados");
      let carrito = [];

      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) carrito = parsed;
          else if (Array.isArray(parsed?.items)) carrito = parsed.items;
        } catch {
          carrito = [];
        }
      }

      if (carrito.some((p) => p.id === this.id)) {
        const { notify } = require("@/utils/notify");
        notify("Este producto ya está en el carrito", { type: 'warn' });
        return;
      }

      const producto = {
        id: this.id,
        nombre: this.nombre,
        precio: Number(this.precio),
        imagen: this.imagen,
        stock: this.cantidad, // ✅ STOCK REAL
        cantidad: 1, // ✅ DEFAULT
      };

      carrito.push(producto);

      localStorage.setItem("productosSeleccionados", JSON.stringify(carrito));

      this.$emit("agregar", producto);
    },
    datosEditar() {
      const editado = {
        id: this.id,
        ...this.editable,
      };
      this.$emit("editar", editado);
      this.mostrarModal = false;
    },
  },

 watch: {
  editable: {
    deep: true,
    handler() {
      const agregar = parseInt(this.editable.cantidad_nueva) || 0;
      const bajar = parseInt(this.editable.baja_stock) || 0;

      let nuevaCantidad = this.cantidad + agregar - bajar;

      if (nuevaCantidad < 0) nuevaCantidad = 0;

      this.editable.cantidad = nuevaCantidad;
    },
  },
},


  computed: {
    cantidadEnCarrito() {
      try {
        const raw = localStorage.getItem("productosSeleccionados");
        if (!raw) return 0;

        const parsed = JSON.parse(raw);
        let items = Array.isArray(parsed) ? parsed : parsed?.items || [];

        const prod = items.find((p) => p.id === this.id);
        return prod ? Number(prod.cantidad) || 0 : 0;
      } catch {
        return 0;
      }
    },
  },
};
</script>
